import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { apiClient as api } from "../services/httpClient";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import { colors, typography } from "../theme/designSystem";

const PASSWORD_MAX = 100;

const ChangePassword: React.FC = () => {
  const { user, markPasswordChanged, logout } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const visibilityAdornment = (visible: boolean, toggle: () => void, label: string) => (
    <InputAdornment position="end">
      <IconButton
        onClick={toggle}
        edge="end"
        disabled={saving}
        aria-label={visible ? `Ocultar ${label}` : `Mostrar ${label}`}
        sx={{ color: colors.textSecondary }}
      >
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async () => {
    if (saving) return;
    if (!currentPassword || !newPassword) {
      setError("Preencha a senha atual e a nova senha.");
      return;
    }
    if (newPassword.length < 8) {
      setError("A nova senha deve ter no mínimo 8 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("A confirmação não confere com a nova senha.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("A nova senha deve ser diferente da atual.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const { data } = await api.post<{ token: string }>("/users/change-password", {
        currentPassword,
        newPassword,
      });
      markPasswordChanged(data.token);
      showToast("Senha alterada com sucesso", "success");
      window.location.href = window.location.origin + "/?page=dashboard";
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { description?: string } } })?.response
          ?.data?.description || "Erro ao trocar a senha. Tente novamente.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: colors.background,
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: 4,
          borderRadius: "12px",
          border: `1px solid ${colors.backgroundAlt}`,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: typography.fontWeight.semibold,
            color: colors.textPrimary,
            mb: 1,
          }}
        >
          Trocar senha
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textSecondary, mb: 3 }}
        >
          Sua conta foi criada com uma senha padrão. Defina uma nova senha para
          continuar{user?.email ? ` (${user.email})` : ""}.
        </Typography>

        <TextField
          label="Senha atual"
          type={showCurrent ? "text" : "password"}
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            if (error) setError("");
          }}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: PASSWORD_MAX }}
          InputProps={{
            endAdornment: visibilityAdornment(
              showCurrent,
              () => setShowCurrent((v) => !v),
              "senha atual"
            ),
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Nova senha"
          type={showNew ? "text" : "password"}
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            if (error) setError("");
          }}
          helperText="Mínimo de 8 caracteres."
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: PASSWORD_MAX }}
          InputProps={{
            endAdornment: visibilityAdornment(
              showNew,
              () => setShowNew((v) => !v),
              "nova senha"
            ),
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Confirmar nova senha"
          type={showConfirm ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (error) setError("");
          }}
          error={!!error}
          helperText={error}
          fullWidth
          margin="dense"
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: PASSWORD_MAX }}
          InputProps={{
            endAdornment: visibilityAdornment(
              showConfirm,
              () => setShowConfirm((v) => !v),
              "confirmação da nova senha"
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          sx={{ mb: 3 }}
        />

        <Button
          onClick={handleSubmit}
          disabled={saving}
          variant="contained"
          fullWidth
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : undefined
          }
          sx={{
            backgroundColor: colors.primary,
            textTransform: "none",
            fontWeight: typography.fontWeight.medium,
            py: 1.2,
            "&.Mui-disabled": {
              backgroundColor: colors.primary,
              opacity: 0.7,
              color: "white",
            },
          }}
        >
          {saving ? "Processando..." : "Salvar nova senha"}
        </Button>

        <Button
          onClick={() => logout()}
          disabled={saving}
          variant="text"
          fullWidth
          sx={{ mt: 1, textTransform: "none", color: colors.textSecondary }}
        >
          Sair
        </Button>
      </Paper>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default ChangePassword;
